import { useCallback, useMemo } from 'react';
import { buildPageId } from '../AdminLinkUtils';
import { useActionContext } from '../infrastructure/hooks/UseActionContext';
import {
    ID_MAPPING,
    MONITORED_INFRASTRUCTURE_ADMIN_LIST_DATA_EMPTY_TEXT,
    MONITORED_INFRASTRUCTURE_ADMIN_LIST_DATA_TABLE_ID,
    MONITORED_INFRASTRUCTURE_DATA_SOURCE,
    MONITORED_INFRASTRUCTURE_FUNCTION_NAME,
    MONITORED_INFRASTRUCTURE_INITIAL_SORT_FIELD_INFO,
    MONITORED_INFRASTRUCTURE_TITLE,
    MONITORED_LIST_ENABLE_DISABLE_URLS,
    PLCNAME_FIELD,
    QUERY_LOADER_OPTION,
} from '../infrastructure/InfrastructureConstants';
import { DELETE, DISABLE } from '../../../services/FormDataTableActionService';
import { monitoredInfrastructureColumns } from '../infrastructure/InfrastructureHelper';
import InfrastructureListBase from '../infrastructure/InfrastructureListBase';
import { useMonitoredInfrastructureInit } from './hooks/UseMonitoredInfrastructureInit';
import { useMonitoredInfrastructureDialogs } from './hooks/UseMonitoredInfrastructureDialogs';
import { MonitoredInfrastructureListActions } from './MonitoredInfrastructureListActions';
import { useFilterState } from '../../../stores/FilterStateStore';

/**
 * Monitored Infrastructure List Page
 *
 * Displays a table of monitored infrastructure devices (routers, switches, firewalls, etc.)
 * with actions for managing devices including RESCAN, DELETE, DISABLE, ENABLE, CREATE, and IMPORT.
 *
 * This component uses extracted hooks and components for better maintainability:
 * - useMonitoredInfrastructureInit: Handles initialization logic (polling config, distributed collectors)
 * - useMonitoredInfrastructureDialogs: Manages all dialog states and handlers
 * - MonitoredInfrastructureListActions: Renders action buttons
 *
 * @returns {JSX.Element} Monitored infrastructure list page
 */
export default function MonitoredInfrastructureList() {
    const [filterState] = useFilterState();
    const { contextRef: actionContextRef, updateContext: updateActionContext } = useActionContext();

    // Initialize polling config and distributed collectors check
    const { showPlcColumn } = useMonitoredInfrastructureInit();

    // Action handlers for delete and disable operations
    const executeAction = useCallback(
        (actionType) => {
            const { handleConfirmedAction, tableSelection, tableState } = actionContextRef.current;
            if (handleConfirmedAction) {
                handleConfirmedAction(undefined, actionType, null, filterState, tableSelection, tableState);
            }
        },
        [actionContextRef, filterState]
    );

    const handleDeleteDataTableAction = useCallback(() => executeAction(DELETE), [executeAction]);
    const handleDisableDataTableAction = useCallback(() => executeAction(DISABLE), [executeAction]);

    // Setup dialogs for import, delete, and classifier log
    const { dialogElements, customActionHandler, showClassifierLogDialog, setImportDialogOpen, refresherRef } =
        useMonitoredInfrastructureDialogs({
            onDeleteConfirmed: handleDeleteDataTableAction,
            onDisableConfirmed: handleDisableDataTableAction,
        });

    // Dynamic columns based on distributed collectors status
    const columns = useMemo(() => {
        const baseColumns = monitoredInfrastructureColumns(showClassifierLogDialog);

        // Filter out PLC column if system is not distributed
        if (!showPlcColumn) {
            return baseColumns.filter((column) => column.mapping !== PLCNAME_FIELD);
        }

        return baseColumns;
    }, [showClassifierLogDialog, showPlcColumn]);

    // Render action buttons - called by InfrastructureListBase
    const renderActionButtons = useCallback(
        ({
            isDataTableActionButton,
            dataTableActioning,
            tableSelection,
            selectionData,
            tableState,
            handleConfirmedDataTableAction,
            refresher,
        }) => {
            // Update action context for executeAction handlers
            updateActionContext({
                handleConfirmedAction: handleConfirmedDataTableAction,
                tableSelection,
                tableState,
                filterState,
            });
            refresherRef.current = refresher;

            const handleImportClick = () => {
                setImportDialogOpen(true);
            };

            return (
                <MonitoredInfrastructureListActions
                    isDataTableActionButton={isDataTableActionButton}
                    dataTableActioning={dataTableActioning}
                    tableSelection={tableSelection}
                    tableState={tableState}
                    handleConfirmedDataTableAction={handleConfirmedDataTableAction}
                    onImportClick={handleImportClick}
                    filterState={filterState}
                    selectionData={selectionData}
                />
            );
        },
        [updateActionContext, setImportDialogOpen, refresherRef, filterState]
    );

    const renderFilterElement = <>{dialogElements}</>;

    return (
        <InfrastructureListBase
            title={MONITORED_INFRASTRUCTURE_TITLE}
            pageId={buildPageId(MONITORED_INFRASTRUCTURE_FUNCTION_NAME)}
            tableId={MONITORED_INFRASTRUCTURE_ADMIN_LIST_DATA_TABLE_ID}
            dataSource={MONITORED_INFRASTRUCTURE_DATA_SOURCE}
            columns={columns}
            sorters={MONITORED_INFRASTRUCTURE_INITIAL_SORT_FIELD_INFO}
            emptyText={MONITORED_INFRASTRUCTURE_ADMIN_LIST_DATA_EMPTY_TEXT}
            idMapping={ID_MAPPING}
            actionUrls={MONITORED_LIST_ENABLE_DISABLE_URLS}
            loaderOptions={QUERY_LOADER_OPTION}
            customActionHandler={customActionHandler}
            filterElement={renderFilterElement}
            actionButtonsFn={renderActionButtons}
        />
    );
}
